import { useEffect, useRef } from 'react'
import { Loader2 } from 'lucide-react'
import { useNavigate } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { useAuth } from '@/contexts/auth-context'
import { useAuthActions } from '@convex-dev/auth/react'
import { track } from '@/lib/track'
import { isElectron } from '@/lib/electron'

import { Navbar } from '@/components/homepage/sections/Navbar'
import { Hero } from '@/components/homepage/sections/Hero'
import { Workflow } from '@/components/homepage/sections/Workflow'
import { WorkflowConnect } from '@/components/homepage/sections/WorkflowConnect'
import { Feature } from '@/components/homepage/sections/Feature'
import { Pricing } from '@/components/homepage/sections/Pricing'
import { FAQ } from '@/components/homepage/sections/FAQ'
import { CLISection } from '@/components/homepage/sections/CLISection'
import { CTA } from '@/components/homepage/sections/CTA'
import { HomepageFooter } from '@/components/homepage/sections/HomepageFooter'
import { LOGO_URL, OG_IMAGE_URL, siteUrl } from '@/lib/config/site'

// Legacy marketing homepage. Kept reachable at /oldhome so the long-form
// landing sections (Hero, Workflow, Pricing, FAQ, etc.) remain available for
// ad campaigns and reference. The new canonical homepage is at /.
export default function OldHomePage() {
  const navigate = useNavigate()
  const { isAuthenticated, isLoading } = useAuth()
  const { signIn } = useAuthActions()
  const loadingRef = useRef(false)

  useEffect(() => {
    track('page_view', { path: window.location.pathname })
  }, [])

  // Desktop users land on /login; authed visitors stay here so they can browse
  // the marketing pages if they navigated in deliberately.
  useEffect(() => {
    if (isLoading) return
    if (!isAuthenticated && isElectron()) {
      navigate('/', { replace: true })
    }
  }, [isLoading, isAuthenticated, navigate])

  // Don't flash the landing page for users with an existing session
  const hasAuthToken = Object.keys(localStorage).some(k => k.startsWith('__convexAuthJWT'))
  if (isLoading && hasAuthToken) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  // Never show marketing page on desktop
  if (isElectron()) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const handleSignUp = () => {
    if (loadingRef.current) return
    loadingRef.current = true
    // Toggle loading class on all signup buttons immediately via DOM (avoids full re-render)
    document.querySelectorAll('.signup-btn').forEach(el => {
      el.classList.add('is-loading')
    })
    track('signup.clicked', { location: 'hero' })
    if (isElectron()) {
      navigate('/')
      return
    }
    signIn('google', { redirectTo: `${window.location.origin}/auth` })
  }

  return (
    <div className="min-h-screen bg-background px-4 lg:px-0 overflow-x-hidden">
      <Helmet>
        <title>Footage - AI Video Generator</title>
        <meta name="description" content="Footage is an AI video generator that writes creative briefs, generates clips, and refines every frame so you can focus on your creative vision." />
        <link rel="canonical" href={siteUrl()} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content="Footage - AI Video Generator" />
        <meta property="og:description" content="Footage is an AI video generator that writes creative briefs, generates clips, and refines every frame so you can focus on your creative vision." />
        <meta property="og:url" content={siteUrl()} />
        <meta property="og:image" content={OG_IMAGE_URL} />
        <meta property="og:site_name" content="Footage" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Footage - AI Video Generator" />
        <meta name="twitter:description" content="Footage is an AI video generator that writes creative briefs, generates clips, and refines every frame so you can focus on your creative vision." />
        <meta name="twitter:image" content={OG_IMAGE_URL} />
        <script type="application/ld+json">{JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'WebSite',
          name: 'Footage',
          url: siteUrl(),
          description: 'AI Video Generator. Create, edit, and share stunning videos with AI that handles the entire production process.',
          publisher: {
            '@type': 'Organization',
            name: 'Footage',
            url: siteUrl(),
            logo: { '@type': 'ImageObject', url: LOGO_URL },
          },
        })}</script>
      </Helmet>

      <div className="max-w-7xl mx-auto border-x border-border">
        <Navbar isAuthenticated={isAuthenticated} onSignUp={handleSignUp} />
        <main className="flex flex-col divide-y divide-border pt-16">
          <Hero />
          <Workflow />
          <WorkflowConnect />
          <Feature />
          <CLISection />
          <Pricing isAuthenticated={isAuthenticated} />
          <FAQ />
          <CTA onSignUp={handleSignUp} />
          <HomepageFooter />
        </main>
      </div>
    </div>
  )
}
